import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { yawPitchToPosition } from "@/utils/coordinateSystem";
import type { Panorama } from "@/store/editorStore";
import { StorageService } from "@/services/storageService";
import { useEditorStore } from "@/store/editorStore";

type BacklinkPlacementModalProps = {
  open: boolean;
  targetPanorama: Panorama | null;
  sourcePanoramaName: string | null;
  targetPanoramaName: string | null;
  craftspaceId: string;
  onCancel: () => void;
  onPick: (position: { x: number; y: number; z: number }) => void;
};

type PointerState = {
  isDown: boolean;
  startX: number;
  startY: number;
  lastX: number;
  lastY: number;
  moved: boolean;
};

const getPanoramaSource = (panorama: Panorama | null): string | null => {
  if (!panorama) return null;
  return panorama.thumbnail_url_webp || panorama.thumbnail_url || null;
};

const resolveSignedPanoramaUrl = async (
  panorama: Panorama,
  craftspaceId: string,
): Promise<string | null> => {
  const baseUrl = getPanoramaSource(panorama);
  if (!baseUrl) return null;

  const isBunnyUrl = baseUrl.includes(".b-cdn.net");
  if (!isBunnyUrl) return baseUrl;

  try {
    const data = (panorama.data as Record<string, unknown>) || {};
    const storagePath = typeof data.storage_path === "string" ? data.storage_path : null;
    const mainFile = typeof data.main_file === "string" ? data.main_file : null;
    if (storagePath && mainFile) {
      const combinedPath = `${storagePath.replace(/\/+$/, "")}/${mainFile.replace(/^\/+/, "")}`;
      const result = await StorageService.getPrivateUrl("staging", combinedPath, 3600, craftspaceId);
      return result.data?.signedUrl ?? baseUrl;
    }

    const path = new URL(baseUrl).pathname.replace(/^\/+/, "");
    const result = await StorageService.getPrivateUrl("staging", path, 3600, craftspaceId);
    return result.data?.signedUrl ?? baseUrl;
  } catch (error) {
    console.warn("Failed to refresh bunny signed URL for modal viewer:", error);
    return baseUrl;
  }
};

const BacklinkPanoramaViewer = ({
  panorama,
  craftspaceId,
  onPick,
}: {
  panorama: Panorama | null;
  craftspaceId: string;
  onPick: (position: { x: number; y: number; z: number }) => void;
}) => {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const pointerRef = useRef<PointerState>({
    isDown: false,
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0,
    moved: false,
  });
  const yawRef = useRef(0);
  const pitchRef = useRef(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  const panoramaSource = useMemo(() => getPanoramaSource(panorama), [panorama]);

  useEffect(() => {
    if (!panoramaSource || !panorama) {
      setIsLoading(false);
      setLoadError("No panorama source available.");
      return;
    }
    let isMounted = true;
    setIsLoading(true);
    setLoadError(null);
    const start = async () => {
      const signedUrl = await resolveSignedPanoramaUrl(panorama, craftspaceId);
      if (!isMounted) return;
      setImageUrl(signedUrl ?? panoramaSource);
    };
    void start();

    return () => {
      isMounted = false;
    };
  }, [craftspaceId, panorama, panoramaSource]);

  const handlePointerDown = (event: React.PointerEvent<HTMLImageElement>) => {
    if (!imgRef.current) return;
    const state = pointerRef.current;
    state.isDown = true;
    state.startX = event.clientX;
    state.startY = event.clientY;
    state.lastX = event.clientX;
    state.lastY = event.clientY;
    state.moved = false;
    (event.currentTarget as HTMLDivElement).setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLImageElement>) => {
    const state = pointerRef.current;
    if (!state.isDown) return;

    if (Math.abs(event.clientX - state.startX) > 3 || Math.abs(event.clientY - state.startY) > 3) {
      state.moved = true;
    }

    state.lastX = event.clientX;
    state.lastY = event.clientY;
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLImageElement>) => {
    const state = pointerRef.current;
    if (!state.isDown) return;
    state.isDown = false;

    if (!state.moved && imgRef.current) {
      const rect = imgRef.current.getBoundingClientRect();
      const u = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
      const v = Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height));
      const yaw = u * 360 - 180;
      const pitch = v * 180 - 90;
      const position = yawPitchToPosition(yaw, pitch, 400);
      onPick(position);
    }
  };

  return (
    <div className="relative w-full h-full rounded-lg overflow-hidden">
      {imageUrl && (
        <img
          ref={imgRef}
          src={imageUrl}
          alt="Target panorama thumbnail"
          className="absolute inset-0 w-full h-full object-contain cursor-crosshair"
          onLoad={() => setIsLoading(false)}
          onError={() => {
            setIsLoading(false);
            setLoadError("Failed to load panorama preview.");
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
        />
      )}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-white text-sm">
          Loading target panorama…
        </div>
      )}
      {loadError && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-red-200 text-sm px-4 text-center">
          {loadError}
        </div>
      )}
    </div>
  );
};

export const BacklinkPlacementModal = ({
  open,
  targetPanorama,
  sourcePanoramaName,
  targetPanoramaName,
  craftspaceId,
  onCancel,
  onPick,
}: BacklinkPlacementModalProps) => {
  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? onCancel() : undefined)}>
      <DialogContent className="w-[95vw] h-[95vh] max-w-none bg-gray-900 border border-gray-700 text-white flex flex-col">
        <DialogHeader>
          <DialogTitle>Place return hotspot</DialogTitle>
        </DialogHeader>
        <div className="text-sm text-gray-300">
          Click inside this panorama to place hotspot that returns back
        </div>
        <div className="flex-1 min-h-0 mt-3">
          <BacklinkPanoramaViewer
            panorama={targetPanorama}
            craftspaceId={craftspaceId}
            onPick={onPick}
          />
        </div>
        <DialogFooter className="mt-3">
          <Button type="button" variant="outline" onClick={onCancel}>
            Skip
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
